import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    effect,
    ViewChild,
    Signal,
    input,
} from '@angular/core';
import { IOnboardingEditItem } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { FiOnboardingService } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { FiOnboardingBuilderHelperService } from '../fi-onboarding-builder-helper.service';
import { FiOnboardingBuilderStoreService, FiOnboardingBuilderTabIndex } from '../fi-onboarding-builder-store.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';

@Component({
    selector: 'fi-onboarding-edit',
    templateUrl: './fi-onboarding-edit.component.html',
    styleUrl: './fi-onboarding-edit.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-edit' },
    standalone: false,
})
export class FiOnboardingEditComponent implements OnInit, OnDestroy {
    private fiOnboardingService = inject(FiOnboardingService);
    private appTextService = inject(AppTextService);
    protected fiOnboardingBuilderHelperService = inject(FiOnboardingBuilderHelperService);
    private fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);
    private fiStateService = inject(FiStateService);
    private saveChangesWarningService = inject(SaveChangesWarningService);

    @ViewChild('fiOnboardingEditForm') fiOnboardingEditForm: FiValidatingFormDirective;

    public isCopyMode = input<boolean>();
    private _currentOnboarding: Signal<IOnboardingEditItem> = this.fiOnboardingBuilderStoreService.currentOnboarding;


    private _formValidityUpdateEffect = effect(() => {
        if (this.fiOnboardingBuilderStoreService.updateFormValidity() > 0) {
            this.fiOnboardingEditForm?.updateValidity();
        }
    });

    protected onboardingEditorHeaderText = computed<string>(() => {
        const onboarding = this._currentOnboarding();
        if (this.isCopyMode()) {
            return (
                this.appTextService.get('onboarding.edit_onboarding') +
                ': (' +
                this.appTextService.get('copy.noun') +
                ') ' +
                onboarding.Name
            );
        }
        if (!onboarding?.Id) {
            return this.appTextService.get('onboarding.new_onboarding');
        }
        return `${this.appTextService.get('onboarding.edit_onboarding')}: ${onboarding.Name}`;
    });

    protected selectedTabIndex: Signal<FiOnboardingBuilderTabIndex> =
        this.fiOnboardingBuilderStoreService.selectedTabIndex;

    public ngOnInit(): void {
        this.startWatchingChanges();
    }

    private startWatchingChanges(): void {
        this.saveChangesWarningService.start((isReceivingInitialObject) =>
            isReceivingInitialObject
                ? this.fiOnboardingBuilderStoreService.getInitialOnboarding()
                : this._currentOnboarding(),
        );
    }

    protected onSelectedTabChange(tabIndex: FiOnboardingBuilderTabIndex): void {
        this.fiOnboardingBuilderStoreService.setSelectedTabIndex(tabIndex);
    }

    protected onSaveButtonClick(): void {
        this.fiOnboardingEditForm.submit();
        this.fiOnboardingBuilderStoreService.onFormSubmit();
    }

    protected save(): void {
        this.fiOnboardingBuilderHelperService.resetCurrentOnboardingInvalidModels();
        this.fiOnboardingBuilderStoreService.setOnboardingQuickSelectionFiltersSave();
        this.fiOnboardingService.saveOnboarding(this._currentOnboarding()).then(() => {
            // ?: show success snackbar message
            this.saveChangesWarningService.stop();
            this.fiStateService.go(FiStateNames.Onboarding.List);
        });
    }

    public ngOnDestroy(): void {
        this.fiOnboardingBuilderStoreService.resetState();
        this.fiOnboardingBuilderStoreService.clearElementsCache();
    }
}
